import { useMemo } from "react";

import { useAppShell } from "@/shell/app-shell-context";
import {
  polarityOptions,
  type PolarityInfo,
  type PolarityScale
} from "@/shared/types/polarity";

/**
 * A decisão "quantas polaridades esta empresa ESCOLHE?" mora aqui, e só
 * aqui — irmã de `useShowsCena` (`cena-visibility.tsx`), pelo mesmo
 * motivo: o valor vem de `company.polarity_scale` no `/me`, e cada tela
 * reimplementando a leitura divergiria na primeira mudança.
 *
 * **Escolha ≠ exibição.** A escala governa onde se CLASSIFICA (o editor
 * de menções, as seções da tela de prompts) e, desde 2026-10-18, as
 * OPÇÕES do filtro de polaridade da tela de menções e do recorte do
 * relatório (decisão da dona: "neutro só pode aparecer se tiver na
 * config da empresa" — desfaz, só pro filtro, o "filtro é busca, não
 * classificação" de 2026-08-14). Exibição — rótulo, cor, o chip de um
 * filtro salvo — continua no domínio inteiro (`polarityLabel`/
 * `polarityPalette`/`polarityOptions` crus): menção antiga FAVORAVEL
 * numa empresa que virou 3 níveis continua legível.
 *
 * O `?? 'four_level'` cobre `company == null` (rotas globais) E payload
 * velho de `/me` em cache — o tipo obrigatório em `CompanyContext` pega
 * esquecimento de mapeamento em compilação, mas não protege runtime.
 */
export function usePolarityScale(): PolarityScale {
  const { company } = useAppShell();
  return company?.polarity_scale ?? "four_level";
}

/**
 * As polaridades que a escala da empresa oferece como escolha, na ordem
 * de exibição do domínio (que é o gradiente real: +2, +1, 0, -1, -2 —
 * o recorte de qualquer escala já sai em ordem natural, sem reordenar).
 *
 * A partição vem do campo `scales` servido por `GET /polarities` — a
 * MESMA fonte que o motor de IA lê (`core/polarity.py`). Nenhuma lista
 * de polaridade é escrita à mão no front.
 */
export function usePolarityChoices(): PolarityInfo[] {
  const scale = usePolarityScale();
  // Memoizado pela ESCALA (2026-10-09): devolver um array novo a cada
  // render fazia quem o pusesse nas dependências de um efeito rodar o
  // efeito a cada render — o `UseExistingDialog` da fila de correção
  // batia em `listThemes`/`listKeyMessages` em laço até a cerca de 429,
  // e girava em laço de render no diálogo de polaridade. A lista só muda
  // quando a escala da empresa muda, então é isso que a identidade segue.
  return useMemo(
    () => polarityOptions().filter(option => option.scales.includes(scale)),
    [scale]
  );
}
